import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { TenantModule } from '@cloudia-shared/tenant/tenant.module';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment } from '@envs/environment';
import { TenantService } from '@cloudia-shared/tenant/tenant.service';
import { TranslocoRootModule } from './core/transloco/transloco-root.module';
import { CloudiaApisModule } from '@cloudia-shared/apis/cloudia-apis.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SfCacheInterceptor } from './core/interceptors/sf-cache-interceptor.service';
import { ReactiveFormsModule } from '@angular/forms';
import { CloudiaTitleStrategy } from './core/services/cloudia-title-strategy.service';
import { CV_TENANT_SERVICE, TENANT_CONFIG } from '@tcp/cloudia-utilities';
import { tenantConfig } from '@cloudia-shared/tenant/tenant.config';
import { UserSettingsInitializer } from './core/config/user-settings/user-settings.initializer';
import {
  GoogleTagManagerConfiguration,
  GoogleTagManagerModule,
  GoogleTagManagerService,
} from 'angular-google-tag-manager';

const initializeKeycloak =
  (
    keycloak: KeycloakService,
    userSettingsInitializer: UserSettingsInitializer,
    tagManagerConfig: GoogleTagManagerConfiguration
  ) =>
  async () => {
    await keycloak
      .init({
        config: {
          url: environment.kcUrl,
          realm: (environment.localBackend
            ? 'cloudia-local'
            : environment.kcRealm.cloudia
          ).replace(
            TenantService.cloudiaLegacyTenantName,
            new TenantService().getTenant()
          ),
          clientId: environment.kcClientID,
        },
        initOptions: {
          refreshToken: localStorage.getItem(
            new TenantService().getTenant() + '-refreshToken'
          ),
          checkLoginIframe: false,
        },
        bearerExcludedUrls: ['/assets/i18n'],
      })
      .then(async (auth) => {
        const R_TOKEN_KEY = new TenantService().getTenant() + '-refreshToken';

        if (auth) {
          const refreshToken = keycloak.getKeycloakInstance().refreshToken;
          if (refreshToken) {
            localStorage.setItem(R_TOKEN_KEY, refreshToken);
          }
        } else {
          const inst = keycloak.getKeycloakInstance();
          inst.refreshToken = localStorage.getItem(R_TOKEN_KEY);
          await inst.updateToken(4 * 3600).catch(() => inst.login());
        }
      })
      .then(() => userSettingsInitializer.init())
      .catch((reason) => {
        console.error(reason);
      });
  };

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    // CanAccessModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: false,
    }),
    IonicModule.forRoot(),
    HttpClientModule,
    CloudiaApisModule,
    TranslocoRootModule,
    KeycloakAngularModule,
    TenantModule,
    NgxPaginationModule,
    ReactiveFormsModule,
    ToastrModule.forRoot({ positionClass: 'toast-bottom-right' }),
    GoogleTagManagerModule.forRoot({ id: environment.analyticsTrackingId }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [
        KeycloakService,
        UserSettingsInitializer,
        TranslocoService,
        GoogleTagManagerService,
        ToastrService,
      ],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SfCacheInterceptor,
      multi: true,
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: TitleStrategy,
      useClass: CloudiaTitleStrategy,
    },
    {
      provide: CV_TENANT_SERVICE,
      useExisting: TenantService,
    },
    { provide: TENANT_CONFIG, useValue: tenantConfig },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
